import { Component, Input, OnInit } from '@angular/core';
import { FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { DocumentConstants } from 'app/constants/document.constants';
import { SubstanceTypeEnum } from 'app/enums/substance-type-enum';
import { FormService } from 'app/services/form.service';
import { FormValidator } from 'app/ui-components/form/form-validators/form-validators';
import { fieldPatterns } from 'app/utilities/form.utilities';

@Component({
  selector: 'app-molecular-formula',
  templateUrl: './molecular-formula.component.html',
  styleUrls: ['./molecular-formula.component.scss']
})
export class MolecularFormulaComponent implements OnInit { 
  @Input({required: true}) substanceType!: SubstanceTypeEnum;
  @Input() shouldShowFullTitle!: boolean // Only if single entity/salt shouldn't be displayed on their respective pages.
  molecularFormula!: string; 

  // For readability in the validators
  private FORM_A = 'FormA';
  private FORM_B = 'FormB';
  private FORM_C = 'FormC';
  private FORM_D = 'FormD';
  private FORM_E = 'FormE';
  private FORM_F = 'FormF';

  private MOLECULAR_FORMULA_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_B, this.FORM_C, this.FORM_D, this.FORM_F]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];
  private MOLECULAR_FORMULA_FOR_SALT_VALIDATORS: ValidatorFn[] = [ FormValidator.isRequiredForFormType(this.formService.getCurrentUsanFormType(), [this.FORM_A]), Validators.pattern(fieldPatterns.molecularFormula), FormValidator.validParenthesis()];

  constructor(private formService: FormService) {
  }
  
  ngOnInit(): void {
    this.validatorUpdates();
    const targetControl = this.currentForm.get(this.formFieldName);
  
    if (!targetControl) {
        return;
    }
  
    this.molecularFormula = targetControl.value;
    targetControl.markAsUntouched();
    targetControl.markAsPristine();
  }

  private validatorUpdates() {
    if(this.hasMolecularFormulaFile) {
      this.updateValidators([false, false]);
    } else {
      this.updateValidators([true, false]);
    }
  }

  public clearMolecularFormulaInputField() {
    this.molecularFormula = '';
    this.patchValueToCurrentForm('');
    this.errorCheck();
  }

  public updateMolecularFormula(event: Event) {
    if (event && event.type === 'blur') {
      var target = document.activeElement;
      if (target && target.nodeValue === "cancel") {
        this.molecularFormula = '';
      }
    }
  
    const targetControl = this.currentForm.get(this.formFieldName);

    if (!targetControl) {
        return;
    }
 
    this.errorCheck();
    this.patchValueToCurrentForm(this.molecularFormula);

  }

  private defineValidators(shouldAddValidators: boolean) {
    const targetControl = this.currentForm.controls[this.formFieldName];

    if (!targetControl) {
        return;
    }

    if(shouldAddValidators) {
      targetControl.setValidators(this.formFieldValidators);
    } else {
      targetControl.clearValidators();
      targetControl.setErrors(null);
    }
    
    setTimeout(() => {
        targetControl.updateValueAndValidity();
    });
  }

  public updateValidators(requiredAndRefreshEvent: boolean[]) {
    const targetControl = this.currentForm.get(this.formFieldName);

    if (!targetControl) {
        return;
    }
    
    let isRequired = requiredAndRefreshEvent[0];
    let refreshValidation = requiredAndRefreshEvent[1];

   this.defineValidators(isRequired);

   // Force re-validation if not the initial call
    setTimeout(() => {
        if(refreshValidation)  {
          targetControl.markAsTouched();
          targetControl.markAsDirty();
        }
        targetControl.updateValueAndValidity();
        this.formService.invalidFormFields();
      });
  }

  private errorCheck() {
    const targetControl = this.currentForm.get(this.formFieldName);
    if(!targetControl) {
      return;
    }

    // Force re-validation
    setTimeout(() => {
      targetControl.markAsTouched();
      targetControl.markAsDirty();
      targetControl.updateValueAndValidity();
      this.formService.invalidFormFields();
    });
  }

  get formFieldValidators(): ValidatorFn[] {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return this.MOLECULAR_FORMULA_VALIDATORS;
    }

    if(this.isSingleEntityFormulaInputType) {
      return this.MOLECULAR_FORMULA_SINGLE_ENTITY_VALIDATORS; 
    }
    if(this.isSaltFormulaInputType) {
      return this.MOLECULAR_FORMULA_FOR_SALT_VALIDATORS;
    }

    return [];
  }

  get currentFormValue() {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return this.currentForm.get('molecularFormula')?.value;
    }

    if(this.isSingleEntityFormulaInputType) {
      return this.currentForm.get('molecularFormulaForSingleEntity')?.value; 
    }
    if(this.isSaltFormulaInputType) {
      return this.currentForm.get('molecularFormulaForSaltForm')?.value; 
    }

    if(this.isContactLensMaterialInputType) {
      return this.currentForm.get('monomerAndPolymerFormulas')?.value;
    }
    
    return '';
  }

  private patchValueToCurrentForm(value: string) {
    this.currentForm.patchValue({ [this.formFieldName] : value });
  }

  get isNoneFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.None;
  }

  get isSingleEntityFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.SingleEntity;
  }

  get isSaltFormulaInputType() {
    return this.substanceType === SubstanceTypeEnum.Salt;
  }

  get isContactLensMaterialInputType() {
    return this.substanceType === SubstanceTypeEnum.ContactLensMaterial;
  }

  get isBiologicsInputType() {
    return this.substanceType === SubstanceTypeEnum.Biologics;
  }

  get formFieldName() {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return 'molecularFormula';
    }

    if(this.isSingleEntityFormulaInputType) {
      return 'molecularFormulaForSingleEntity'; 
    }
    if(this.isSaltFormulaInputType) {
      return 'molecularFormulaForSaltForm'; 
    }

    if(this.isContactLensMaterialInputType) {
      return 'monomerAndPolymerFormulas';
    }

    return '';
  }

  get formFieldTitle() {
    var title = 'Molecular formula';

    if(this.isSingleEntityFormulaInputType && this.shouldShowFullTitle) {
      title += ' for single entity'; 
    }
    if(this.isSaltFormulaInputType && this.shouldShowFullTitle) {
      title += ' for salt form'; 
    }

    return title;
  }

  get infoText() {
    if(this.isContactLensMaterialInputType) {
      return 'List the formula for each monomer, and for the whole polymer. If this is a submission for a hybrid lens material, please supply information for both the soft skirt and hard center.'
    }

    if(this.isBiologicsInputType) {
      return 'Not required for cell therapies, for glycosylated peptides or proteins, please list the molecular formula for the peptide component, if known. If calculated, please indicate the types of calculations used. If the formula cannot be calculated or is indeterminate, please indicate what assumptions or calculations were used to determine the molecular formula and attach a file with this information.';
    }

    return 'Use correct element symbols and subscript numbers (e.g., H2O, CH4). No spaces';
  }

  get currentForm(): FormGroup {
    return this.formService.getCurrentUsanFormValue();
  }

  public hasFieldValidationError(fieldName: string, errorName?: string): boolean {
    return this.formService.hasFieldValidationError(fieldName, errorName);
  }

  get hasMolecularFormulaFile(): boolean {
    return this.formService.doesCurrentFormHaveAttachmentForSection(this.attachmentNamePrefix);
  }

  get attachmentNamePrefix() {
    if(this.isNoneFormulaInputType || this.isBiologicsInputType) {
      return DocumentConstants.MOLECULAR_FORMULA;
    }

    if(this.isSingleEntityFormulaInputType) {
      return DocumentConstants.MOLECULAR_FORMULA_SINGLE_ENTITY;
    }
    if(this.isSaltFormulaInputType) {
      return DocumentConstants.MOLECULAR_FORMULA_FOR_SALT;
    }

    if(this.isContactLensMaterialInputType) {
      return DocumentConstants.MONOMER_AND_POLYMER_FORMULAS;
    }

    return '';
  }
}
